import React from 'react';
import { useTranslation } from 'react-i18next';
import { Copy } from 'lucide-react';
import { rnQpUserLabel, RnQpDecision, RnQpReleaseTransfer } from '../types/rnQpRequests';

export const CHECKLIST_KEYS = [
  'approvedNormative',
  'deviationsClosedOrAssessed',
  'changeControlClosed',
  'coaInSpecification',
  'rnRequiredFieldsComplete',
  'batchAndExpiryChecked',
  'equipmentCalibrated',
  'lineAndCleaningChecked',
  'materialsReleased',
  'inProcessControlsOk',
  'yieldChecked',
  'noAffectingOpenComplaint',
];

export interface ChecklistItem {
  key: string;
  checked: boolean;
}

interface Props {
  /** Existing decision on the thread — when present, the checklist/PIN form is replaced by a read-only summary. */
  decision?: RnQpDecision | null;
  /** System transfer created with an approved decision — shown as text only (no route). */
  releaseTransfer?: RnQpReleaseTransfer | null;
  checklist: ChecklistItem[];
  onChecklistToggle: (key: string, checked: boolean) => void;
  pin: string;
  reason: string;
  onPinChange: (value: string) => void;
  onReasonFocus: () => void;
  onReasonChange: (value: string) => void;
  canApprove: boolean;
  canReject: boolean;
  onApprove: () => void;
  onReject: () => void;
  formatDateTime: (value?: string) => string;
}

const RnQpDecisionPanel: React.FC<Props> = ({
  decision,
  releaseTransfer,
  checklist,
  onChecklistToggle,
  pin,
  reason,
  onPinChange,
  onReasonFocus,
  onReasonChange,
  canApprove,
  canReject,
  onApprove,
  onReject,
  formatDateTime,
}) => {
  const { t } = useTranslation();

  if (decision?.decision) {
    return (
      <div className="border-t border-gray-200 bg-gray-50 px-3 py-2 text-xs">
        <div className="font-semibold">
          {t(`rnQpRequests.${decision.decision}`)} · {rnQpUserLabel(decision.decidedBy) || ''} · {formatDateTime(decision.decidedAt)}
        </div>
        {decision.reason && (
          <div className="mt-1 whitespace-pre-wrap">{decision.reason}</div>
        )}
        {releaseTransfer?.number && (
          <div className="mt-1 flex items-center gap-1 text-gray-700">
            <span>{t('rnQpRequests.releaseTransfer')}: </span>
            <span className="font-mono font-semibold">{releaseTransfer.number}</span>
            <button
              type="button"
              onClick={() => navigator.clipboard?.writeText(releaseTransfer.number)}
              title={t('rnQpRequests.copyNumber')}
              aria-label={t('rnQpRequests.copyNumber')}
              className="rounded p-0.5 text-gray-500 hover:bg-blue-50 hover:text-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500/50"
            >
              <Copy size={12} />
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="border-t border-gray-200 px-3 py-3">
      <div className="mb-2 text-xs font-semibold text-gray-700">{t('rnQpRequests.checklistTitle')}</div>

      <div className="grid grid-cols-1 gap-x-4 gap-y-1 sm:grid-cols-2">
        {checklist.map((item) => (
          <label key={item.key} className="flex items-start gap-2 text-[11px] text-gray-700">
            <input
              type="checkbox"
              checked={item.checked}
              onChange={(e) => onChecklistToggle(item.key, e.target.checked)}
              className="mt-0.5 accent-blue-600 focus:ring-blue-500"
            />
            {t(`rnQpRequests.checklist.${item.key}`)}
          </label>
        ))}
      </div>

      <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-3">
        <input
          type="password"
          autoComplete="new-password"
          value={pin}
          onChange={(e) => onPinChange(e.target.value)}
          placeholder={t('rnQpRequests.signaturePin')}
          className="rounded-md border border-gray-300 px-2 py-1.5 text-xs focus:outline-none focus:bg-blue-50 focus:ring-2 focus:ring-blue-500/50"
        />
        <input
          value={reason}
          onFocus={onReasonFocus}
          onChange={(e) => onReasonChange(e.target.value)}
          placeholder={t('rnQpRequests.reason')}
          className="rounded-md border border-gray-300 px-2 py-1.5 text-xs focus:outline-none focus:bg-blue-50 focus:ring-2 focus:ring-blue-500/50"
        />
        <div className="flex gap-2">
          <button
            type="button"
            disabled={!canApprove}
            onClick={onApprove}
            className="rounded bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white disabled:cursor-not-allowed disabled:bg-gray-300"
          >
            {t('rnQpRequests.approve')}
          </button>
          <button
            type="button"
            disabled={!canReject}
            onClick={onReject}
            className="rounded border border-red-300 bg-white px-3 py-1.5 text-xs font-semibold text-red-700 disabled:cursor-not-allowed disabled:text-gray-400"
          >
            {t('rnQpRequests.reject')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RnQpDecisionPanel;
